import ReactMarkdown, { type Components } from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Link } from 'react-router-dom'
import type { ReactNode } from 'react'

// Markdown renderer for vault bodies, narrative passages, and essays. Every
// element is styled to the Nordic-ledger system (no @tailwindcss/typography
// dependency — hand-rolled per DESIGN-BRIEF §1's minimal-components rule).
//
// variant="narrative" switches to Source Serif 4 (§4: serif for NAR passages
// and discourse essays). Internal links (href starting "/") route through
// react-router so the SPA never does a full reload.

type ProseVariant = 'default' | 'narrative'

interface ProseProps {
  children: string
  variant?: ProseVariant
  className?: string
}

const linkClass =
  'text-accent underline decoration-hairline underline-offset-2 hover:decoration-accent'

function Anchor({ href, children }: { href?: string; children?: ReactNode }) {
  if (href && href.startsWith('/')) {
    return (
      <Link to={href} className={linkClass}>
        {children}
      </Link>
    )
  }
  const external = !!href && /^https?:/.test(href)
  return (
    <a href={href} className={linkClass} {...(external ? { rel: 'noreferrer' } : {})}>
      {children}
    </a>
  )
}

const components: Components = {
  h1: ({ children }) => <h1 className="mb-3 mt-8 text-3xl font-semibold tracking-tight">{children}</h1>,
  h2: ({ children }) => <h2 className="mb-3 mt-8 text-2xl font-semibold tracking-tight">{children}</h2>,
  h3: ({ children }) => <h3 className="mb-2 mt-6 text-xl font-semibold">{children}</h3>,
  h4: ({ children }) => <h4 className="mb-2 mt-4 text-lg font-semibold">{children}</h4>,
  p: ({ children }) => <p className="my-4 leading-relaxed">{children}</p>,
  a: Anchor,
  ul: ({ children }) => <ul className="my-4 list-disc space-y-1 pl-5">{children}</ul>,
  ol: ({ children }) => <ol className="my-4 list-decimal space-y-1 pl-5">{children}</ol>,
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold text-ink">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  blockquote: ({ children }) => (
    <blockquote className="my-4 border-l-2 border-accent pl-4 italic text-muted">{children}</blockquote>
  ),
  hr: () => <hr className="my-8 rule-hairline" />,
  code: ({ className, children }) => {
    const isBlock = /language-/.test(className ?? '')
    if (isBlock) {
      return <code className="font-mono text-sm">{children}</code>
    }
    return (
      <code className="rounded-sm bg-accent-soft px-1 py-0.5 font-mono text-[0.85em]">{children}</code>
    )
  },
  pre: ({ children }) => (
    <pre className="my-4 overflow-x-auto rounded border border-hairline bg-[#F4F4EF] p-4 text-sm leading-relaxed">
      {children}
    </pre>
  ),
  table: ({ children }) => (
    <div className="my-4 overflow-x-auto">
      <table className="w-full border-collapse text-sm">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="border-b border-hairline text-left">{children}</thead>,
  th: ({ children }) => <th className="px-2 py-1.5 font-semibold">{children}</th>,
  td: ({ children }) => <td className="border-b border-hairline px-2 py-1.5 align-top">{children}</td>,
}

export function Prose({ children, variant = 'default', className = '' }: ProseProps) {
  const variantClass = variant === 'narrative' ? 'font-serif text-lg text-ink' : 'font-sans text-ink'
  return (
    <div className={`max-w-prose ${variantClass} ${className}`}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {children}
      </ReactMarkdown>
    </div>
  )
}
